import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { AdvShell } from "@/components/AdvShell";
import { Metric, StatusBadge, money, num } from "./advUtils";
import { Plus, ArrowUpRight, CheckCircle2, Circle, X } from "lucide-react";

export default function AdvDashboard() {
  const [d, setD] = useState(null);
  const [camps, setCamps] = useState([]);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem("adv_onboard_dismissed") === "1");
  useEffect(() => {
    api.get("/adv/dashboard").then((r) => setD(r.data)).catch(() => {});
    api.get("/adv/campaigns").then((r) => setCamps(r.data)).catch(() => {});
  }, []);

  const hasPM = !!d?.has_payment_method;
  const hasCampaign = camps.length > 0;
  const hasSubmitted = camps.some((c) => c.status !== "draft");
  const steps = [
    { k: "pm", label: "Add a payment method", desc: "You're only billed for ad spend you actually deliver.", done: hasPM, to: "/advertiser/billing", cta: "Add card" },
    { k: "create", label: "Create your first campaign", desc: "Design your ad and choose who sees it.", done: hasCampaign, to: "/advertiser/campaigns/new", cta: "Create" },
    { k: "submit", label: "Submit for review", desc: "Send it to the IBRA team to go live.", done: hasSubmitted, to: hasCampaign ? "/advertiser/campaigns" : "/advertiser/campaigns/new", cta: "Submit" },
  ];
  const doneCount = steps.filter((s) => s.done).length;
  const allDone = doneCount === steps.length;
  const showChecklist = !!d && !allDone && !dismissed;
  const dismiss = () => { localStorage.setItem("adv_onboard_dismissed", "1"); setDismissed(true); };
  const firstOpen = steps.find((s) => !s.done)?.k;

  return (
    <AdvShell>
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="font-heading text-4xl tracking-tight" data-testid="adv-dashboard-title">Advertiser Dashboard</h1>
          <p className="text-race-sub mt-1">Manage your Text Racing campaigns, creative and performance.</p>
        </div>
        <Link to="/advertiser/campaigns/new" data-testid="adv-dashboard-create" className="inline-flex items-center gap-2 bg-race-volt text-race-bg font-semibold text-sm px-4 py-2.5 rounded-md hover:opacity-90"><Plus size={16} /> Create Campaign</Link>
      </div>

      {showChecklist && (
        <div className="mb-8 rounded-xl border border-race-volt/30 bg-race-surface p-5" data-testid="adv-onboarding">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h2 className="font-heading text-xl tracking-tight">Get set up in 3 steps</h2>
              <p className="text-race-sub text-sm mt-0.5">{doneCount} of {steps.length} complete — finish to launch your first campaign.</p>
            </div>
            <button onClick={dismiss} data-testid="adv-onboarding-dismiss" className="text-race-muted hover:text-race-txt shrink-0" title="Dismiss"><X size={18} /></button>
          </div>
          <div className="h-1.5 rounded-full bg-race-bg overflow-hidden mb-4">
            <div className="h-full bg-race-volt transition-all duration-500" style={{ width: `${(doneCount / steps.length) * 100}%` }} />
          </div>
          <div className="space-y-2">
            {steps.map((s, i) => (
              <div key={s.k} data-testid={`adv-onboarding-step-${s.k}`}
                className={`flex items-center gap-3 rounded-lg border p-3 ${s.done ? "border-race-line/60 opacity-70" : s.k === firstOpen ? "border-race-volt/50 bg-race-volt/5" : "border-race-line"}`}>
                {s.done ? <CheckCircle2 size={20} className="text-race-volt shrink-0" /> : <Circle size={20} className="text-race-muted shrink-0" />}
                <div className="min-w-0 flex-1">
                  <div className={`text-sm font-semibold ${s.done ? "line-through text-race-sub" : "text-race-txt"}`}>{i + 1}. {s.label}</div>
                  {!s.done && <div className="text-xs text-race-sub">{s.desc}</div>}
                </div>
                {!s.done && s.k === firstOpen && (
                  <Link to={s.to} data-testid={`adv-onboarding-cta-${s.k}`} className="shrink-0 text-xs font-semibold bg-race-volt text-race-bg px-3 py-1.5 rounded-md hover:opacity-90">{s.cta}</Link>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        <Metric testid="adv-m-active" label="Active Campaigns" value={num(d?.active_campaigns)} />
        <Metric testid="adv-m-spend" label="Total Spend" value={money(d?.total_spend)} sub={`Billed ${money(d?.total_billed)} · Unbilled ${money(d?.unbilled)}`} />
        <Metric testid="adv-m-remaining" label="Remaining Budget" value={money(d?.remaining_budget)} />
        <Metric testid="adv-m-impressions" label="Impressions" value={num(d?.impressions)} />
        <Metric testid="adv-m-clicks" label="Clicks" value={num(d?.clicks)} />
        <Metric testid="adv-m-ctr" label="CTR" value={`${d?.ctr ?? 0}%`} />
      </div>

      {!showChecklist && !hasPM && (
        <div className="mb-8 rounded-lg border border-race-volt/40 bg-race-volt/5 px-5 py-4 flex items-center justify-between gap-4" data-testid="adv-add-pm-nudge">
          <div className="text-sm"><span className="font-semibold text-race-txt">Add a payment method</span><span className="text-race-sub"> — required before you can submit a campaign. You're only billed for delivered ad spend.</span></div>
          <Link to="/advertiser/billing" className="shrink-0 text-sm font-semibold text-race-volt hover:underline">Go to Billing →</Link>
        </div>
      )}

      <div className="flex items-center justify-between mb-3">
        <h2 className="font-heading text-xl tracking-tight">Recent campaigns</h2>
        <Link to="/advertiser/campaigns" className="text-sm text-race-sub hover:text-race-txt inline-flex items-center gap-1">View all <ArrowUpRight size={14} /></Link>
      </div>
      {camps.length === 0 ? (
        <div className="rounded-lg border border-dashed border-race-line py-16 text-center" data-testid="adv-empty-campaigns">
          <p className="text-race-sub">No campaigns yet.</p>
          <Link to="/advertiser/campaigns/new" className="inline-flex items-center gap-2 mt-4 bg-race-volt text-race-bg font-semibold text-sm px-4 py-2.5 rounded-md"><Plus size={16} /> Create your first campaign</Link>
        </div>
      ) : (
        <div className="space-y-2">
          {camps.slice(0, 5).map((c) => (
            <Link key={c.id} to={`/advertiser/campaigns/${c.id}`} data-testid={`adv-dash-camp-${c.id}`}
              className="flex items-center gap-4 bg-race-surface border border-race-line rounded-lg p-3 hover:border-race-volt/50 transition-colors">
              <div className="w-24 h-12 rounded bg-race-bg border border-race-line overflow-hidden shrink-0">
                {c.image_url && <img src={c.image_url.startsWith("http") ? c.image_url : `${process.env.REACT_APP_BACKEND_URL}${c.image_url}`} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="min-w-0 flex-1"><div className="font-semibold truncate">{c.name}</div><div className="text-xs text-race-sub">{money(c.spend_usd)} spent · {num(c.impressions)} impressions</div></div>
              <StatusBadge status={c.status} label={c.status_label} />
            </Link>
          ))}
        </div>
      )}
    </AdvShell>
  );
}
